import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { FAQS, PHONE, PHONE_HREF } from "./data/content";
import { scrollToId } from "../lib/scroll";
import { trackEvent } from "../lib/tracking";
import Reveal from "./Reveal";

export const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faqs" className="border-y border-white/10 bg-surface/50 py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="label-mono">FAQs</p>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                Answers, plainly.
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-slate-400">
                Can&rsquo;t find what you&rsquo;re looking for? Ask us directly — every conversation is
                confidential.
              </p>
              <a
                href={PHONE_HREF}
                className="mt-4 inline-block font-mono text-sm text-gold-light transition-colors hover:text-white"
                data-testid="faq-phone-link"
              >
                {PHONE}
              </a>
              <button
                onClick={() => {
                  trackEvent("cta_click", { location: "faq", target: "contact" });
                  scrollToId("contact");
                }}
                data-testid="faq-cta-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-3 text-sm font-semibold text-gold-light transition-all duration-200 hover:bg-gold hover:text-slate-950"
              >
                Ask your question
                <ArrowRight className="h-4 w-4" />
              </button>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={f.q} delay={Math.min(i * 0.03, 0.25)}>
                  <div className="border-b border-white/10">
                    <button
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`faq-question-${i + 1}`}
                      className="flex w-full items-center justify-between gap-6 py-6 text-left"
                    >
                      <span className={`font-display text-base font-semibold tracking-tight sm:text-lg ${isOpen ? "text-gold-light" : "text-white"}`}>
                        {f.q}
                      </span>
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                          isOpen ? "rotate-45 border-gold bg-gold/15 text-gold-light" : "border-white/15 text-slate-400"
                        }`}
                      >
                        <Plus className="h-4 w-4" />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="max-w-2xl pb-6 text-sm leading-relaxed text-slate-400 sm:text-base">
                            {f.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
